/**
 * Cliente API para los endpoints del Catalogador IA (admin).
 * Backend: server/routes/catalogAdminRoutes.js
 */
import api from '../utils/api';

// Providers soportados actualmente. Mantenemos `openai`/`gemini` en el tipo
// solo para compatibilidad con versiones legacy guardadas en DB; el UI sólo
// permite seleccionar `cerebras` y `openrouter`.
export type Provider = 'openai' | 'gemini' | 'cerebras' | 'openrouter';

export const ACTIVE_PROVIDERS: Provider[] = ['cerebras', 'gemini', 'openrouter'];
export const LEGACY_PROVIDERS: Provider[] = ['openai'];

export type SyncSource =
  | 'distinct_marca'
  | 'distinct_cat'
  | 'distinct_tag_value'
  | 'firestore_brandHierarchy'
  | 'static';

export interface PromptVariable {
  type: 'list';
  values: string[];
  syncSource: SyncSource;
  syncParam?: string | null;
  lastSyncedAt?: string | null;
}

export interface CatalogPrompt {
  id: string;
  /**
   * Versión semver "X.Y" alineada al catalogador (ej "5.1", "4.0").
   *
   * Por compatibilidad con docs legacy pre-migración, puede venir como `number`
   * — el front debe normalizar con `parseSemverVersion`. Cuando todos los
   * clientes pasaron por `migrate-prompt-versions.js --apply`, el campo es
   * siempre string semver.
   */
  version: string | number;
  name?: string | null;
  prompt: string;
  provider: Provider;
  model: string;
  temperature: number;
  maxTokens: number;
  confidenceThreshold: number;
  /** Cantidad de llamados al LLM en paralelo durante batch. Default 5. */
  concurrency?: number;
  createdAt: string;
  createdBy: string | null;
  isActive: boolean;
  /**
   * CAMBIO #5: archived true = oculta del listado principal, mostrable en
   * sub-sección colapsable. DELETE solo permitido sobre archivadas.
   */
  archived?: boolean;
  updatedAt?: string;
  updatedBy?: string | null;
  variables?: Record<string, PromptVariable>;
}

export type VersionBump = 'minor' | 'major' | 'explicit';

export interface PromptDraft {
  prompt: string;
  name?: string;
  provider: Provider;
  model: string;
  temperature: number;
  maxTokens: number;
  confidenceThreshold: number;
  /** Cantidad de llamados al LLM en paralelo durante batch. Default 5. */
  concurrency?: number;
  activate?: boolean;
  /** Default 'minor'. */
  versionBump?: VersionBump;
  /** Solo cuando versionBump === 'explicit'. Formato "X.Y". */
  versionExplicit?: string;
  /**
   * Versión base de la cual heredar `variables`, tokens custom, etc.
   * Si se omite, el backend cae al prompt con `isActive: true` (frágil cuando
   * el flag no está bien seteado). Para "Guardar como nueva versión" el
   * frontend pasa explícitamente la versión seleccionada.
   */
  basedOnVersion?: string | number;
}

// ---------- Helpers semver ----------

/**
 * Parsea `version` (string "X.Y" o number legacy) a `[major, minor]`.
 * Devuelve `[0, 0]` si no parsea.
 */
export function parseSemverVersion(value: unknown): [number, number] {
  if (typeof value === 'string') {
    const m = value.match(/^(\d+)\.(\d+)$/);
    if (m) return [parseInt(m[1], 10), parseInt(m[2], 10)];
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return [value, 0];
  }
  return [0, 0];
}

export function formatSemverVersion(value: unknown): string {
  const [M, m] = parseSemverVersion(value);
  return `${M}.${m}`;
}

/** Comparator descendente para sort. */
export function compareSemverDesc(a: unknown, b: unknown): number {
  const [aM, am] = parseSemverVersion(a);
  const [bM, bm] = parseSemverVersion(b);
  if (bM !== aM) return bM - aM;
  return bm - am;
}

/** Próxima versión "minor" basada en una activa. */
export function nextMinor(value: unknown): string {
  const [M, m] = parseSemverVersion(value);
  return `${M}.${m + 1}`;
}

/** Próxima versión "major" basada en una activa. */
export function nextMajor(value: unknown): string {
  const [M] = parseSemverVersion(value);
  return `${M + 1}.0`;
}

export interface ValidationResult {
  passedWhitelist: boolean;
  belowThreshold: boolean;
  hallucinationsDetected: boolean;
  categoryNotLeaf: boolean;
  notes: string[];
  // CAMBIO #1 — flags brand "Toma manda"
  from_llm?: boolean;
  not_whitelisted?: boolean;
  whitelisted?: boolean;
  consistent?: boolean;
  toma_only?: boolean;
  toma_not_whitelisted?: boolean;
  disagreement?: boolean;
  disagreement_value?: string | null;
  // CAMBIO #2 — subcategory track-only
  subcategory_not_in_matrix?: boolean;
  subcategory_suggested?: string | null;
  // V2 extras
  forced_to_otros?: boolean;
  udm_overridden?: boolean;
  pizarras_forced?: boolean;
}

export interface TestItemResult {
  id_catalogo: string | number;
  title: string;
  imagen: string | null;
  marcaOriginal: string | null;
  catOriginal?: string | null;
  // Fila cruda completa de la toma — usada por el banco de pruebas para
  // mostrar campos nativos (sitio, segmento, contenido, udm, url, etc.) sin
  // pedir el schema al backend. Solo viene populado en modo streaming.
  product?: Record<string, any>;
  llmOutput?: any;
  final?: {
    catalog_brand?: string | null;
    catalog_category?: string | null;
    confidence_score?: number | null;
    reasoning?: string | null;
    segmento?: string | null;
    contenido?: string | null;
    udm?: string | null;
    evidence?: any;
    [k: string]: any;
  };
  validation?: ValidationResult;
  tokenUsage?: { input_tokens: number; output_tokens: number; total_tokens: number; model: string };
  costUsd?: number;
  model?: string;
  durationMs?: number;
  error?: string;
}

export interface TestBatchResponse {
  promptVersion: string | number;
  promptId: string;
  provider: Provider;
  model: string;
  results: TestItemResult[];
  resultsB?: TestItemResult[] | null;
  secondary?: { provider: Provider; model: string } | null;
}

export interface RunDoc {
  runId: string;
  clientId: string;
  // 'test'    : run del Banco de pruebas (no escribe a MariaDB).
  // 'batch'   : run sobre productos pendientes (`mode='pending'`).
  // 'recatalog': re-cataloga TODOS los productos (`mode='all'`).
  // Aliases legacy del backend pueden venir como 'manual_test'.
  runType: 'test' | 'batch' | 'recatalog' | 'manual_test';
  triggeredBy: string | null;
  triggeredAt: string;
  completedAt: string | null;
  promptVersion: string | number;
  promptName?: string | null;
  model: { provider: Provider; name: string; temperature: number; maxTokens: number };
  tableName: string;
  status: 'processing' | 'completed' | 'cancelled' | 'error';
  productsTotal: number;
  productsProcessed: number;
  productsFailed: number;
  productsSkipped: number;
  tokensIn: number;
  tokensOut: number;
  costUsd: number;
  validation: { passedWhitelist: number; belowThreshold: number; hallucinationsDetected: number; categoryNotLeaf: number };
  errorMessage?: string;
  // 'full' (default): LLM + persist a MariaDB en el mismo run.
  // 'llm-only': solo LLM, sin persistir (para batches en paralelo).
  // 'upload-only': bulk INSERT a MariaDB usando datos de runs previos (sin LLM).
  executionMode?: 'full' | 'llm-only' | 'upload-only';
}

const headers = (clientId: string) => ({ 'x-client-id': clientId });

export async function listPrompts(clientId: string): Promise<{ prompts: CatalogPrompt[] }> {
  const r = await api.get(`/api/clients/${clientId}/catalog/prompts`, { headers: headers(clientId) });
  return r.data;
}

export async function createPrompt(clientId: string, body: PromptDraft): Promise<{ prompt: CatalogPrompt }> {
  const r = await api.post(`/api/clients/${clientId}/catalog/prompts`, body, { headers: headers(clientId) });
  return r.data;
}

export async function activatePrompt(clientId: string, version: string | number): Promise<{ success: boolean }> {
  const r = await api.patch(`/api/clients/${clientId}/catalog/prompts/${encodeURIComponent(String(version))}/activate`, {}, { headers: headers(clientId) });
  return r.data;
}

export interface TestBatchBody {
  productIds: (string | number)[];
  tableName: string;
  promptVersion?: string | number;
  secondary?: { provider: Provider; model: string; temperature?: number; maxTokens?: number; confidenceThreshold?: number };
}

export async function testBatch(clientId: string, body: TestBatchBody): Promise<TestBatchResponse> {
  const r = await api.post(`/api/clients/${clientId}/catalog/test-batch`, body, { headers: headers(clientId), timeout: 600000 });
  return r.data;
}

// ---------------------------------------------------------------------------
// Streaming variant (NDJSON) del banco de pruebas
// ---------------------------------------------------------------------------

export type TestStreamEvent =
  | { type: 'start'; total: number; runId?: string; promptVersion: string | number; promptId: string; provider: Provider; model: string; secondary: any }
  | { type: 'result'; side: 'A' | 'B'; item: TestItemResult; processedCount: number }
  | { type: 'error'; side?: 'A' | 'B'; id_catalogo?: string | number; error: string }
  | { type: 'done'; totals: { a: number; b: number } };

/**
 * Arranca un test-batch en modo NDJSON streaming. Llama a `onEvent` por cada
 * objeto JSON parseado del stream. Devuelve `{ cancel }` para abortar.
 *
 * Usa fetch directo (no axios) porque axios en browser no soporta streaming
 * con `ReadableStream`. Reproducimos los headers del interceptor manualmente.
 */
export async function streamTestBatch(
  clientId: string,
  body: TestBatchBody,
  onEvent: (e: TestStreamEvent) => void,
): Promise<{ cancel: () => void }> {
  // Reproducir los headers que normalmente agrega el interceptor de axios.
  const { auth } = await import('../config/firebase');
  const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;

  const isDev = process.env.NODE_ENV === 'development';
  // REACT_APP_API_URL toma precedencia SIEMPRE (ver src/utils/api.ts).
  const baseURL = process.env.REACT_APP_API_URL
    ? process.env.REACT_APP_API_URL
    : (isDev ? 'http://localhost:3001' : '');

  const controller = new AbortController();

  const reqHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    'x-client-id': clientId,
    'Accept': 'application/x-ndjson',
  };
  if (token) reqHeaders['Authorization'] = `Bearer ${token}`;

  // Iniciamos sin await para que el caller pueda llamar cancel inmediatamente
  // si lo necesita; el procesamiento del stream se hace en una promesa interna.
  const fetchPromise = fetch(`${baseURL}/api/clients/${clientId}/catalog/test-batch?stream=1`, {
    method: 'POST',
    headers: reqHeaders,
    body: JSON.stringify(body),
    signal: controller.signal,
  });

  // Manejo asíncrono del stream
  (async () => {
    let response: Response;
    try {
      response = await fetchPromise;
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        onEvent({ type: 'error', error: err.message });
      }
      return;
    }

    if (!response.ok || !response.body) {
      let txt = '';
      try { txt = await response.text(); } catch {}
      onEvent({ type: 'error', error: `HTTP ${response.status}: ${txt.slice(0, 300)}` });
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Procesar líneas completas (newline-delimited JSON)
        let nl = buffer.indexOf('\n');
        while (nl !== -1) {
          const line = buffer.slice(0, nl).trim();
          buffer = buffer.slice(nl + 1);
          if (line) {
            try {
              const obj = JSON.parse(line);
              onEvent(obj as TestStreamEvent);
            } catch {
              // línea no parseable; ignorar
            }
          }
          nl = buffer.indexOf('\n');
        }
      }
      // Flush último chunk
      if (buffer.trim()) {
        try { onEvent(JSON.parse(buffer.trim()) as TestStreamEvent); } catch {}
      }
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        onEvent({ type: 'error', error: err.message });
      }
    }
  })();

  return { cancel: () => controller.abort() };
}

export interface RunBatchBody {
  tableName: string;
  mode?: 'pending' | 'all';
  promptVersion?: string | number;
  productIds?: (string | number)[];
}

export async function runBatch(clientId: string, body: RunBatchBody): Promise<{ runId: string }> {
  const r = await api.post(`/api/clients/${clientId}/catalog/run-batch`, body, { headers: headers(clientId) });
  return r.data;
}

export async function listRuns(clientId: string, params?: { limit?: number; runType?: string; status?: string }): Promise<{ runs: RunDoc[] }> {
  const r = await api.get(`/api/clients/${clientId}/catalog/runs`, { headers: headers(clientId), params });
  return r.data;
}

export async function getRun(clientId: string, runId: string): Promise<RunDoc> {
  const r = await api.get(`/api/clients/${clientId}/catalog/runs/${runId}`, { headers: headers(clientId) });
  return r.data;
}

export async function getRunItems(
  clientId: string,
  runId: string,
  opts: { limit?: number; cursor?: string | null; filter?: string } = {},
): Promise<{ items: any[]; nextCursor: string | null; hasMore: boolean }> {
  const { limit = 100, cursor = null, filter } = opts;
  const params: Record<string, any> = { limit };
  if (cursor) params.cursor = cursor;
  if (filter && filter !== 'all') params.filter = filter;
  const r = await api.get(
    `/api/clients/${clientId}/catalog/runs/${runId}/items`,
    { headers: headers(clientId), params },
  );
  return r.data;
}

export async function cancelRun(clientId: string, runId: string): Promise<{ success: boolean }> {
  const r = await api.post(`/api/clients/${clientId}/catalog/runs/${runId}/cancel`, {}, { headers: headers(clientId) });
  return r.data;
}

/**
 * Marca un run zombi (status='processing' sin runner activo) como cancelled
 * directamente en Firestore. Distinto de cancelRun: aquel depende del runner
 * activo. Este escribe el doc directo.
 */
export async function markRunCancelled(
  clientId: string,
  runId: string,
  reason?: string,
): Promise<{ success: boolean }> {
  const r = await api.post(
    `/api/clients/${clientId}/catalog/runs/${runId}/mark-cancelled`,
    reason ? { reason } : {},
    { headers: headers(clientId) },
  );
  return r.data;
}

export interface EnrichedTomaRow {
  id_catalogo: string | number;
  title?: string | null;
  imagen?: string | null;
  url?: string | null;
  Marca?: string | null;
  Cat?: string | null;
  sitio?: string | null;
}

/**
 * Enriquecimiento on-demand de un item de un run: lee la fila correspondiente
 * en la tabla `toma_*` para recuperar `imagen` / `url` que no quedaron persistidos
 * en el itemDoc de Firestore (caso de runs viejos).
 */
export async function getRunItemEnrichment(
  clientId: string,
  runId: string,
  idCatalogo: string | number
): Promise<{ row: EnrichedTomaRow | null }> {
  const r = await api.get(
    `/api/clients/${clientId}/catalog/runs/${runId}/items/${encodeURIComponent(String(idCatalogo))}/enrich`,
    { headers: headers(clientId) }
  );
  return r.data;
}

export const PROVIDER_MODELS: Record<Provider, string[]> = {
  // Legacy — no se ofrecen en el dropdown pero los dejamos para no romper
  // versiones antiguas que llegan del backend.
  openai: ['gpt-4.1-mini'],
  // Activos
  cerebras: [
    'gpt-oss-120b',
    'llama3.1-8b',
  ],
  gemini: [
    'gemini-2.0-flash',
    'gemini-2.5-flash',
    'gemini-2.0-flash-exp',
  ],
  openrouter: [
    'meta-llama/llama-4-scout',
    'meta-llama/llama-4-maverick',
    'meta-llama/llama-3.3-70b-instruct',
    'google/gemma-3-27b-it',
  ],
};

export const PROVIDER_LABELS: Record<Provider, string> = {
  openai: 'OpenAI (legacy)',
  cerebras: 'Cerebras',
  gemini: 'Google Gemini',
  openrouter: 'OpenRouter',
};

// ---------- Provider model lists (loaded from backend) ----------

export interface ProviderModel {
  id: string;
  name: string;
  vendor: string;
  context_length: number;
  pricing_input_per_1m: number;
  pricing_output_per_1m: number;
  supports_vision: boolean;
  recommended_for_catalog: boolean;
}

export interface ProviderModelsResponse {
  lastUpdatedAt: string | null;
  models: ProviderModel[];
  source?: string;
  fromCache?: boolean;
  fallbackReason?: string;
  totalAvailable?: number;
  afterFilter?: number;
}

export async function fetchProviderModels(
  provider: 'openrouter' | 'cerebras',
  refresh = false,
): Promise<ProviderModelsResponse> {
  const r = await api.get(`/api/admin/catalog/${provider}-models`, {
    params: refresh ? { refresh: 1 } : {},
  });
  return r.data;
}

// ---------- Variables del prompt ----------

export interface VariableSyncDiff {
  varName: string;
  syncSource: SyncSource;
  syncParam: string | null;
  source: string;
  current: string[];
  proposed: string[];
  added: string[];
  removed: string[];
  unchanged: string[];
}

/** POST sync — propone valores nuevos sin escribir. */
export async function syncPromptVariable(
  clientId: string,
  version: string | number,
  varName: string,
  body: { syncSource: SyncSource; syncParam?: string | null },
): Promise<VariableSyncDiff> {
  const r = await api.post(
    `/api/clients/${clientId}/catalog/prompts/${encodeURIComponent(String(version))}/variables/${encodeURIComponent(varName)}/sync`,
    body,
    { headers: headers(clientId) },
  );
  return r.data;
}

/** PATCH variable — actualiza values (replace) o agrega valores (addValues). */
export async function patchPromptVariable(
  clientId: string,
  version: string | number,
  varName: string,
  body: {
    values?: string[];
    addValues?: string[];
    syncSource?: SyncSource;
    syncParam?: string | null;
  },
): Promise<{ success: boolean; varName: string; variable: PromptVariable }> {
  const r = await api.patch(
    `/api/clients/${clientId}/catalog/prompts/${encodeURIComponent(String(version))}/variables/${encodeURIComponent(varName)}`,
    body,
    { headers: headers(clientId) },
  );
  return r.data;
}

// ---------- Marcas sugeridas (hallucinations) ----------

export interface BrandSuggestion {
  id: string;
  suggestedBrand: string;
  count: number;
  exampleTitles: string[];
  firstSeen?: string;
  lastSeen?: string;
  ignored?: boolean;
}

export async function listBrandSuggestions(
  clientId: string,
  params?: { limit?: number; includeIgnored?: boolean },
): Promise<{ clientId: string; suggestions: BrandSuggestion[] }> {
  const r = await api.get(`/api/clients/${clientId}/catalog/suggested-brands`, {
    headers: headers(clientId),
    params: {
      limit: params?.limit,
      includeIgnored: params?.includeIgnored ? 'true' : undefined,
    },
  });
  return r.data;
}

export async function patchBrandSuggestion(
  clientId: string,
  suggestionId: string,
  body: { ignored?: boolean },
): Promise<{ success: boolean }> {
  const r = await api.patch(
    `/api/clients/${clientId}/catalog/suggested-brands/${encodeURIComponent(suggestionId)}`,
    body,
    { headers: headers(clientId) },
  );
  return r.data;
}

// ---------- CAMBIO #6: Prompt templates (empty state) ----------

export interface PromptTemplate {
  id: string;
  label: string;
  prompt: string;
}

export async function listPromptTemplates(clientId: string): Promise<{ templates: PromptTemplate[] }> {
  const r = await api.get(`/api/clients/${clientId}/catalog/prompt-templates`, {
    headers: headers(clientId),
  });
  return r.data;
}

// ---------- CAMBIO #2: Subcategory suggestions ----------

export interface SubcategorySuggestion {
  id: string;
  suggestedSubcategory: string;
  count: number;
  exampleTitles: string[];
  firstSeen?: string;
  lastSeen?: string;
  ignored?: boolean;
}

export async function listSubcategorySuggestions(
  clientId: string,
  params?: { limit?: number; includeIgnored?: boolean },
): Promise<{ clientId: string; suggestions: SubcategorySuggestion[] }> {
  const r = await api.get(`/api/clients/${clientId}/catalog/suggested-subcategories`, {
    headers: headers(clientId),
    params: {
      limit: params?.limit,
      includeIgnored: params?.includeIgnored ? 'true' : undefined,
    },
  });
  return r.data;
}

export async function patchSubcategorySuggestion(
  clientId: string,
  slug: string,
  body: { ignored?: boolean },
): Promise<{ success: boolean }> {
  const r = await api.patch(
    `/api/clients/${clientId}/catalog/suggested-subcategories/${encodeURIComponent(slug)}`,
    body,
    { headers: headers(clientId) },
  );
  return r.data;
}

// ---------- CAMBIO #5: Archive / Delete prompt versions ----------

export async function archivePrompt(
  clientId: string,
  version: string | number,
  archived: boolean,
): Promise<{ success: boolean; archived: boolean }> {
  const r = await api.patch(
    `/api/clients/${clientId}/catalog/prompts/${encodeURIComponent(String(version))}/archive`,
    { archived },
    { headers: headers(clientId) },
  );
  return r.data;
}

export async function deletePrompt(
  clientId: string,
  version: string | number,
): Promise<{ success: boolean }> {
  const r = await api.delete(
    `/api/clients/${clientId}/catalog/prompts/${encodeURIComponent(String(version))}`,
    { headers: headers(clientId) },
  );
  return r.data;
}

// ---------- CAMBIO #9: PATCH active prompt (Option C — mutable) ----------

export interface PatchActivePromptBody {
  prompt?: string;
  name?: string;
  provider?: Provider;
  model?: string;
  temperature?: number;
  maxTokens?: number;
  confidenceThreshold?: number;
  concurrency?: number;
}

/**
 * Option C — mutable active version. PATCH la versión activa: pisa los campos
 * provistos sin crear nueva versión. Si la version no es activa → 403.
 */
export async function patchActivePrompt(
  clientId: string,
  version: string | number,
  body: PatchActivePromptBody,
): Promise<{ success: boolean; prompt: CatalogPrompt }> {
  const r = await api.patch(
    `/api/clients/${clientId}/catalog/prompts/${encodeURIComponent(String(version))}`,
    body,
    { headers: headers(clientId) },
  );
  return r.data;
}

// ---------- CAMBIO #3 + #8: Brand Curation Reports ----------

export interface CurationReportBrandEntry {
  brand: string;
  count: number;
  examples: string[];
}

export interface CurationReportDisagreement {
  tomaBrand: string;
  llmBrand: string;
  count: number;
  examples: string[];
}

export interface CurationReportSubcatSuggestion {
  suggested: string;
  count: number;
  examples: string[];
}

export interface CurationReport {
  id?: string;
  runId: string;
  runDate: string;
  runType: string;
  promptVersion: string | number | null;
  promptName: string | null;
  totalProducts: number;
  brandsFromLlmOnly: {
    whitelisted: CurationReportBrandEntry[];
    notWhitelisted: CurationReportBrandEntry[];
  };
  brandsDisagreed: CurationReportDisagreement[];
  brandsConsistent: number;
  brandsTomaOnly: number;
  brandsTomaNotWhitelisted: CurationReportBrandEntry[];
  brandsAllNull: number;
  subcategorySuggestions: CurationReportSubcatSuggestion[];
  status: 'pending_review' | 'sent' | 'archived';
  generatedAt?: any;
}

export async function listCurationReports(
  clientId: string,
  params?: { limit?: number },
): Promise<{ clientId: string; reports: CurationReport[] }> {
  const r = await api.get(`/api/clients/${clientId}/catalog/curation-reports`, {
    headers: headers(clientId), params,
  });
  return r.data;
}

export async function getCurationReport(
  clientId: string, runId: string,
): Promise<CurationReport> {
  const r = await api.get(`/api/clients/${clientId}/catalog/curation-reports/${encodeURIComponent(runId)}`, {
    headers: headers(clientId),
  });
  return r.data;
}

export async function patchCurationReport(
  clientId: string, runId: string,
  body: { status: 'pending_review' | 'sent' | 'archived' },
): Promise<{ success: boolean }> {
  const r = await api.patch(
    `/api/clients/${clientId}/catalog/curation-reports/${encodeURIComponent(runId)}`,
    body,
    { headers: headers(clientId) },
  );
  return r.data;
}

// ---------- Inline edit de campos editables del catalog ----------
// Backend: PATCH /api/clients/:id/catalog/:idCatalogo (server/utils/catalogPatch.js)
// Whitelist: catalog_brand | catalog_category | subcategory | segmento | contenido | udm

export async function patchCatalogField(
  clientId: string,
  idCatalogo: string | number,
  field: string,
  value: string | null,
): Promise<{ success: boolean; row: Record<string, unknown> }> {
  const r = await api.patch(
    `/api/clients/${clientId}/catalog/${encodeURIComponent(String(idCatalogo))}`,
    { [field]: value },
    { headers: headers(clientId) },
  );
  return r.data;
}
